import { useEffect, useState } from 'react';

interface HeroProps {
  lang: 'uk' | 'ru';
}

const content = {
  uk: {
    badge: 'Сертифікований психолог · Марбелья',
    title: 'Допомагаю знайти внутрішню гармонію та впевненість',
    desc: 'Маю вищу психологічну освіту та сертифікати міжнародного зразка. Працюю в гештальт-підході. Спеціалізуюся на роботі з дорослими людьми та підлітками',
    cta: 'Записатися на консультацію',
    years: 'років практики',
    clients: 'клієнтів',
    alt: 'Ірина Сергеєнко — психолог',
  },
  ru: {
    badge: 'Сертифицированный психолог · Марбелья',
    title: 'Помогаю найти внутреннюю гармонию и уверенность',
    desc: 'Имею высшее психологическое образование и сертификаты международного образца. Работаю в гештальт-подходе. Специализируюсь на работе со взрослыми людьми и подростками',
    cta: 'Записаться на консультацию',
    years: 'лет практики',
    clients: 'клиентов',
    alt: 'Ирина Сергеенко — психолог',
  },
};

export default function Hero({ lang }: HeroProps) {
  const t = content[lang];
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-b from-white to-[#FFF9F4] dark:from-[#121212] dark:to-[#1A1510] pt-16 md:pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full py-12 md:py-0">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Image block */}
          <div
            className={`relative order-2 lg:order-2 transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="relative rounded-[24px] md:rounded-[32px] overflow-hidden shadow-[0_8px_40px_rgba(126,200,227,0.15)] dark:shadow-[0_8px_40px_rgba(126,200,227,0.08)]">
              <img
                src="https://static.readdy.ai/image/c26179b2c08d8211833738b4663bf6e2/189f5759872ba913a4a9f0a2368c4f2d.jpeg"
                alt={t.alt}
                className="w-full h-auto object-cover object-top"
              />
            </div>

            {/* Stats card */}
            <div
              className={`absolute -bottom-4 md:-bottom-6 left-1/2 transform -translate-x-1/2 bg-white dark:bg-[#1E1E1E] rounded-xl md:rounded-2xl shadow-lg dark:shadow-[0_4px_24px_rgba(0,0,0,0.4)] px-5 md:px-8 py-3 md:py-5 flex items-center gap-3 md:gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '700ms' }}
            >
              <div className="text-center">
                <div className="text-xl md:text-2xl font-semibold text-[#7EC8E3]">15+</div>
                <div className="text-xs md:text-sm text-[#666666] dark:text-[#AAAAAA] whitespace-nowrap">{t.years}</div>
              </div>
              <div className="w-px h-10 md:h-12 bg-[#E5E5E5] dark:bg-[#333333]"></div>
              <div className="text-center">
                <div className="text-xl md:text-2xl font-semibold text-[#7EC8E3]">300+</div>
                <div className="text-xs md:text-sm text-[#666666] dark:text-[#AAAAAA] whitespace-nowrap">{t.clients}</div>
              </div>
            </div>
          </div>

          {/* Text block */}
          <div className="space-y-6 md:space-y-8 order-1 lg:order-1">
            <div
              className={`inline-block px-4 md:px-5 py-2 border border-[#7EC8E3] dark:border-[#5AAFC8] rounded-full transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '0ms' }}
            >
              <span className="text-[13px] md:text-[14px] text-[#7EC8E3] dark:text-[#7EC8E3]">{t.badge}</span>
            </div>

            <h1
              className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-[#333333] dark:text-[#EEEEEE] leading-tight transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '150ms' }}
            >
              {t.title}
            </h1>

            <p
              className={`text-base md:text-xl text-[#666666] dark:text-[#AAAAAA] leading-relaxed max-w-xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '300ms' }}
            >
              {t.desc}
            </p>

            <div
              className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '450ms' }}
            >
              <button
                onClick={scrollToContact}
                className="group flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 bg-[#7EC8E3] text-white rounded-full text-[15px] md:text-[16px] font-medium hover:bg-[#6BB5D0] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <span>{t.cta}</span>
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-whatsapp-line text-[#7EC8E3] text-lg md:text-xl"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
